import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import Layout from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Schedule = lazy(() => import('./pages/Schedule'));
const Contracts = lazy(() => import('./pages/Contracts'));
const ContractDetails = lazy(() => import('./pages/ContractDetails'));
const Finance = lazy(() => import('./pages/Finance'));
const Budgets = lazy(() => import('./pages/Budgets'));
const Receipts = lazy(() => import('./pages/Receipts'));
const Providers = lazy(() => import('./pages/Providers'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const Reports = lazy(() => import('./pages/Reports'));
const BIDashboard = lazy(() => import('./pages/BIDashboard'));
const Mentor = lazy(() => import('./pages/Mentor'));
const IA = lazy(() => import('./pages/IA'));
const Settings = lazy(() => import('./pages/Settings'));
const Login = lazy(() => import('./pages/Login'));

function RouteFallback() {
  return <div className="sr-only">Carregando módulo...</div>;
}

function RestrictedRoute({ element }: { element: React.ReactElement }) {
  const { role } = useAuth();
  if (role === 'funcionario') {
    return <Navigate to="/" replace />;
  }
  return element;
}

function ProtectedApp() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div className="min-h-screen bg-brand-bg" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <DataProvider>
      <Layout>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/agenda" element={<Schedule />} />
            <Route path="/contratos" element={<Contracts />} />
            <Route path="/contratos/:id" element={<ContractDetails />} />
            <Route path="/financeiro" element={<RestrictedRoute element={<Finance />} />} />
            <Route path="/orcamentos" element={<Budgets />} />
            <Route path="/recibos" element={<Receipts />} />
            <Route path="/prestadores" element={<Providers />} />
            <Route path="/fornecedores" element={<Suppliers />} />
            <Route path="/relatorios" element={<RestrictedRoute element={<Reports />} />} />
            <Route path="/relatorios/:id" element={<RestrictedRoute element={<BIDashboard />} />} />
            <Route path="/mentor" element={<RestrictedRoute element={<Mentor />} />} />
            <Route path="/ia" element={<RestrictedRoute element={<IA />} />} />
            <Route path="/configuracoes" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </DataProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/*" element={<ProtectedApp />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
